import { useEffect, useRef, useState, type ReactNode } from "react"

import { Card } from "@coval/ui/card"
import { Tooltip } from "@coval/ui/tooltip"

import type { CriterionResult } from "./composite-evaluation-types"

interface CompositeBehaviorCardProps {
  criterion: CriterionResult
  index: number
  showAiResult: boolean
  aiVerdict: ReactNode
  humanReview: ReactNode
  references: ReactNode
}

export function CompositeBehaviorCard({
  criterion,
  index,
  showAiResult,
  aiVerdict,
  humanReview,
  references,
}: CompositeBehaviorCardProps) {
  const quoteRef = useRef<HTMLParagraphElement>(null)
  const [isClipped, setIsClipped] = useState(false)

  useEffect(() => {
    const node = quoteRef.current
    if (!node) {
      return
    }
    const measure = () => {
      setIsClipped(
        node.clientWidth > 0 && node.scrollHeight > node.clientHeight + 1,
      )
    }
    measure()
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure)
    observer?.observe(node)
    return () => observer?.disconnect()
  }, [criterion.criterion])

  return (
    <Card role="group" aria-label={`Expected behavior ${index + 1}`}>
      <div className="border-b border-coval-border p-3">
        <Tooltip
          content={`“${criterion.criterion}”`}
          openOnInteraction={isClipped}
        >
          <p
            ref={quoteRef}
            // Clipped text becomes the tooltip's trigger, with the role Radix
            // gives a TooltipTrigger along with its tab stop.
            {...(isClipped ? { role: "button", tabIndex: 0 } : {})}
            className="line-clamp-3 whitespace-pre-wrap wrap-break-word text-base font-bold text-coval-text"
          >
            “{criterion.criterion}”
          </p>
        </Tooltip>
      </div>
      <div
        className={
          showAiResult
            ? "grid grid-cols-1 @lg/criteria:grid-cols-2"
            : "grid grid-cols-1"
        }
      >
        {showAiResult && (
          <section
            aria-label={`AI Judge for behavior ${index + 1}`}
            className="min-w-0 space-y-3 border-b border-coval-border p-3 @lg/criteria:border-b-0 @lg/criteria:border-r"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-base font-semibold text-coval-text">
                AI Judge
              </h4>
              {aiVerdict}
            </div>
            {criterion.explanation && (
              <p className="whitespace-pre-wrap wrap-break-word text-base italic text-coval-text">
                {criterion.explanation}
              </p>
            )}
            {criterion.matching_message_indices?.length ? (
              <div
                role="group"
                aria-label={`References for behavior ${index + 1}`}
                className="space-y-1.5"
              >
                <h5 className="text-xs font-medium text-coval-text-muted">
                  References
                </h5>
                <div className="flex flex-wrap items-center gap-1.5">
                  {references}
                </div>
              </div>
            ) : null}
          </section>
        )}
        <section
          aria-label={`Human review for behavior ${index + 1}`}
          className="min-w-0 space-y-3 p-3"
        >
          <h4 className="text-base font-semibold text-coval-text">Human</h4>
          {humanReview}
        </section>
      </div>
    </Card>
  )
}
